import {
  type PlatformCapabilitiesQueryHookResult,
  usePlatformCapabilitiesQuery
} from '../__generated__/apollo-hooks';

type ProfilingSession = {
  user: { email: string; username?: string | null };
  startedAt: Date;
};

type CapabilityProfiling = {
  deployed: boolean;
  isActive: boolean;
  session: ProfilingSession | null;
};

type Capabilities = {
  connector: boolean;
  changelog: boolean;
  codeSearch: boolean;
  moddy: boolean;
  /** Recipes run on the platform. Off where they run only from the CLI. */
  recipeRuns: boolean;
  /** Changesets can be committed and pull requests opened from the platform. */
  commits: boolean;
  visualizations: boolean;
  audit: boolean;
  profiling: CapabilityProfiling;
  loading: boolean;
  error: PlatformCapabilitiesQueryHookResult['error'];
  refetch: PlatformCapabilitiesQueryHookResult['refetch'];
};

const EMPTY_PROFILING: CapabilityProfiling = {
  deployed: false,
  isActive: false,
  session: null
};

export const useCapabilities = (): Capabilities => {
  const { data, loading, error, refetch } = usePlatformCapabilitiesQuery({
    fetchPolicy: 'cache-first'
  });

  if (!data) {
    return {
      connector: false,
      changelog: false,
      codeSearch: false,
      moddy: false,
      // Every SaaS tenant has these; only a deployment without the service (Moderne
      // DX) turns one off. Presuming them on until the answer arrives keeps the
      // Dry run and Commit buttons from flickering out on every tenant. A failed
      // query leaves them on too, so callers that can't act on a guess check `error`.
      recipeRuns: true,
      commits: true,
      visualizations: true,
      audit: true,
      profiling: EMPTY_PROFILING,
      loading,
      error,
      refetch
    };
  }

  const session = data.capabilities.profiling.session ?? null;

  return {
    connector: data.capabilities.connector,
    changelog: data.capabilities.changelog,
    codeSearch: data.capabilities.codeSearch,
    moddy: data.capabilities.moddy,
    recipeRuns: data.capabilities.recipeRuns,
    commits: data.capabilities.commits,
    visualizations: data.capabilities.visualizations,
    audit: data.capabilities.audit,
    profiling: {
      deployed: data.capabilities.profiling.deployed,
      isActive: session !== null,
      session
    },
    loading,
    error,
    refetch
  };
};
